import React, { useState, useEffect } from 'react';
import { User, Building, Briefcase, Crown, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

interface AdminPromotionFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  inline?: boolean;
}

export default function AdminPromotionForm({ onSuccess, onCancel, inline = false }: AdminPromotionFormProps) {
  const [formData, setFormData] = useState({
    authKey: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registeredName, setRegisteredName] = useState<string>('');

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const email = user?.email;
      if (!email) return;
      const { data: cand } = await supabase.from('candidates').select('name').eq('email', email).maybeSingle();
      if (cand?.name) { setRegisteredName(cand.name); return; }
      const { data: evalr } = await supabase.from('evaluators').select('name').eq('email', email).maybeSingle();
      if (evalr?.name) { setRegisteredName(evalr.name); }
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // 認証キーの検証
      const ADMIN_AUTH_KEY = import.meta.env.VITE_ADMIN_AUTH_KEY || 'ADMIN2025KEY';
      if (formData.authKey !== ADMIN_AUTH_KEY) {
        throw new Error('認証キーが正しくありません');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      if (registeredName && formData.name.trim() !== registeredName) {
        throw new Error('入力した氏名が登録名と一致しません');
      }

      // 管理者レコードを作成
      const { error: adminError } = await supabase
        .from('admin_users')
        .insert({
          user_id: user.id
        });

      if (adminError) throw adminError;

      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={inline ? '' : 'min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'}>
      <div className={inline ? '' : 'max-w-md w-full space-y-6'}>
        <div className="text-center">
          <Crown className="mx-auto h-12 w-12 text-yellow-600" />
          <h2 className="mt-6 text-2xl sm:text-3xl font-bold text-foreground">
            管理者昇格
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">認証キーと氏名を入力してください（氏名は登録名と一致が必要）</p>
        </div>

        {error && (
          <Card className="border-destructive/50 bg-destructive/10">
            <CardContent className="pt-6">
              <p className="text-sm text-destructive">{error}</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>管理者申請</CardTitle>
            <CardDescription>認証キーは運営から提供されます。氏名は登録名と一致している必要があります。</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="auth-key">認証キー *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="auth-key"
                    type="password"
                    required
                    value={formData.authKey}
                    onChange={(e) => setFormData({...formData, authKey: e.target.value})}
                    className="pl-10"
                    placeholder="管理者認証キーを入力"
                  />
                </div>
                <p className="text-xs text-muted-foreground">管理者から提供された認証キーを入力してください</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-name">氏名 *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="admin-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="pl-10"
                    placeholder="山田太郎"
                  />
                </div>
              </div>
              {registeredName && (
                <p className="text-xs text-muted-foreground">登録名: {registeredName}</p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  className="flex-1"
                  disabled={loading}
                >
                  キャンセル
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? '昇格中...' : '管理者になる'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {!inline && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <Crown className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-yellow-800">
                  管理者認証について
                </p>
                <p className="text-xs text-yellow-700">
                  管理者昇格には事前に提供された認証キーが必要です。認証キーをお持ちでない場合は、システム管理者にお問い合わせください。
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}
